if (!customElements.get('td-article-sticky-bar')) {
  const SELECTORS = {
    root: '[data-td-article]',
    hero: '[data-td-sticky-hero]',
    end: '[data-td-sticky-end]',
    focusTarget: '[data-td-sticky-focus]',
  };
  const END_OFFSET = 80;
  const END_REACH_ABOVE = 100000;
  const VISIBLE_CLASS = 'td-main-article__sticky--visible';

  class TdArticleStickyBar extends HTMLElement {
    connectedCallback() {
      // The theme editor can reconnect the same element; drop old observers first.
      this.teardown();
      const root = this.closest(SELECTORS.root);
      const hero = root?.querySelector(SELECTORS.hero);
      // The card, plus an end marker after the article when the card is hidden on
      // mobile. A display: none card never intersects, so the marker decides there.
      const ends = root?.querySelectorAll(SELECTORS.end);
      if (!hero || !ends?.length) return;
      this.focusTarget = root.querySelector(SELECTORS.focusTarget);

      this.heroPassed = false;
      this.endsReached = new Set();

      // Shown once the hero has fully left the top of the viewport.
      this.heroObserver = new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1];
        this.heroPassed = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.update();
      });

      // Hidden once the end card is within END_OFFSET of the viewport bottom, and
      // for the rest of the page below it. The root extends far above the viewport
      // so a card scrolled past the top still counts as intersecting; otherwise a
      // fast jump from above the card to the footer would fire no callback.
      this.endObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this.endsReached.add(entry.target);
            else this.endsReached.delete(entry.target);
          });
          this.update();
        },
        { rootMargin: `${END_REACH_ABOVE}px 0px -${END_OFFSET}px 0px` }
      );

      this.heroObserver.observe(hero);
      ends.forEach((end) => this.endObserver.observe(end));
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      this.heroObserver?.disconnect();
      this.endObserver?.disconnect();
      this.heroObserver = null;
      this.endObserver = null;
      this.endsReached?.clear();
    }

    update() {
      const visible = this.heroPassed && this.endsReached.size === 0;
      // inert drops focus to <body>; hand it to the end card's button instead,
      // when the card is shown at this breakpoint.
      if (!visible && this.contains(document.activeElement) && this.focusTarget?.getClientRects().length) {
        this.focusTarget.focus({ preventScroll: true });
      }
      this.classList.toggle(VISIBLE_CLASS, visible);
      this.toggleAttribute('inert', !visible);
      this.setAttribute('aria-hidden', String(!visible));
    }
  }

  customElements.define('td-article-sticky-bar', TdArticleStickyBar);
}
